import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { KIND } from "baseui/button";
import { ConfirmDialog } from "components/confirm-dialog";
import { Content } from "components/content";
import { FormRenderer } from "components/form-renderer";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { useAuth } from "contexts/auth-context";
import { useDictionaries } from "contexts/dictionaries-context";
import { useLoading } from "contexts/loading-context";
import { useProject } from "contexts/project-context";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { DeviceFloppy, FileOff } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";
import { scrollOnError } from "utils/scrollOnError";

import { FormType } from "../../../constants";
import { CustomFile } from "../files";
import { FILES_FIELDS, FilesFormInputs } from "../files.form";
import { FILES_SHOW, FILES_UPDATE } from "../files.gql";

export default function FilesUpdate(): React.ReactElement {
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(
    false
  );
  const { enqueueSnackbar } = useSnackbar();
  const { setIsFetching, isLoading, setIsLoading } = useLoading();
  const { checkPermission } = useAuth();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const { activeProject, activeProjectId } = useProject();
  const { findValue } = useDictionaries();
  const [originalProjectId, setOriginalProjectId] = useState<number>();

  const {
    control,
    formState: { errors, isDirty, dirtyFields, ...formState },
    handleSubmit,
    setValue,
    reset,
    watch,
    ...methods
  } = useForm<FilesFormInputs>();

  const { refetch, data, error: queryError } = useQuery(FILES_SHOW, {
    variables: { id: id ? parseInt(id) : null },
  });
  const file: CustomFile = data?.file;

  useEffect(() => {
    setIsFetching(true);

    if (data?.file) refetch();
  }, []);

  useEffect(() => {
    if (queryError?.graphQLErrors)
      enqueueSnackbar({
        message: (queryError as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
  }, [queryError]);

  useEffect(() => {
    async function setFile() {
      setValue("file", [
        ({ ...file, name: file?.filename } as unknown) as File,
      ]);

      setIsFetching(false);
    }

    if (file) {
      setOriginalProjectId(file?.project?.id);

      setValue("project", [{ ...file?.project, id: file?.project?.id }]);
      setValue("language", [{ id: file?.language }]);
      setValue("name", file?.name);
      setValue("isVisible", file?.isVisible);

      if (file?.url) {
        setFile();
      } else {
        setIsFetching(false);
      }
    }
  }, [data]);

  useEffect(() => {
    !activeProject &&
      setValue("project", [{ ...file?.project, id: file?.project?.id }]);
  }, [activeProject]);

  const watchProject = watch("project");

  useEffect(() => {
    if (
      originalProjectId === watchProject?.[0]?.id ||
      originalProjectId === activeProjectId
    )
      setValue("language", [
        {
          id: file?.language,
          label: (findValue(file?.language as string) as unknown) as string,
        },
      ]);
    else setValue("language", []);
  }, [watchProject, activeProject]);

  const [errorTimeStamp, setErrorTimeStamp] = useState<number>();

  const onError = (errors: Record<string, unknown>, e: any) =>
    setErrorTimeStamp(e.timeStamp);

  useEffect(() => {
    if (errors) scrollOnError(errors, FILES_FIELDS, FormType.Update);
  }, [errorTimeStamp]);

  const [updateFile, { error }] = useMutation(FILES_UPDATE);

  const onSubmit = async ({
    project,
    language,
    isVisible,
    file,
    ...values
  }: FilesFormInputs): Promise<void> => {
    setIsLoading(true);

    const shouldSendFile = !!dirtyFields?.file;

    try {
      await updateFile({
        variables: {
          fileUpdateInput: {
            id: id ? parseInt(id) : null,
            projectId: activeProjectId || project?.[0]?.id,
            language: language?.[0]?.id,
            isVisible: !!isVisible,
            ...(shouldSendFile && { file: file[0] }),
            ...values,
          },
        },
      });
      enqueueSnackbar({
        message: "Zapisano pomyślnie",
        variant: "success",
      });
      history.push(`/files/${id}`);
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!checkPermission(PERMISSIONS.file.update))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title={file?.name}
        labels={["Pliki", "Edytowanie"]}
        goBackOption
        buttons={[
          {
            label: "Anuluj",
            kind: KIND.secondary,
            startEnhancer: <FileOff size={18} />,
            disabled: isLoading,
            onClick: () =>
              isDirty ? setIsCancelConfirmDialogOpen(true) : history.goBack(),
          },
          {
            label: "Zapisz",
            kind: KIND.primary,
            startEnhancer: <DeviceFloppy size={18} />,
            onClick: handleSubmit(onSubmit, onError),
            disabled: isLoading,
            isLoading: isLoading,
            type: "submit",
            formId: "updateFile",
          },
        ]}
      />
      <Content>
        <FormProvider<FilesFormInputs>
          control={control}
          formState={{ errors, isDirty, dirtyFields, ...formState }}
          handleSubmit={handleSubmit}
          reset={reset}
          setValue={setValue}
          watch={watch}
          {...methods}
        >
          <FormRenderer<FilesFormInputs>
            id="updateFile"
            onSubmit={handleSubmit(onSubmit, onError)}
            type="update"
            isLoading={isLoading}
            error={error}
            fields={FILES_FIELDS}
          />
        </FormProvider>
        <ConfirmDialog
          isOpen={isCancelConfirmDialogOpen}
          label="Anulowanie edycji pliku"
          close={() => setIsCancelConfirmDialogOpen(false)}
          confirm={() => history.goBack()}
        />
      </Content>
    </article>
  );
}
